import { fromCompositeId, toCompositeId } from "helpers/compositeId";
import { startCase } from "lodash";
import { userRoleEmailId } from "helpers/string";
import { CheckpointTemplate } from "./models";
import { CheckpointTemplateSchema } from "./schema";
import { CreateCheckpointTemplatePayload, UpdateCheckpointTemplatePayload } from "./models/payloads";

export function checkpointTemplatePrimaryTrigger(template: CheckpointTemplate) {
  return template?.evaluateAtTrigger;
}

/**
 * Convert composite ID strings (e.g. "User:123") to RecordTarget objects
 * for API submission. Passes through objects that are already in target format.
 */
function normalizeRecipients(recipients: any[]): { targetType: string; targetId: string }[] {
  return (recipients || [])
    .map((r: any) => {
      if (typeof r === "string") {
        const { resourceType, resourceId } = fromCompositeId(r);
        return { targetType: resourceType || "", targetId: resourceId || "" };
      }
      // Handle both RecordTarget format (targetType/targetId) and
      // UserRoleOrEmailMultiSelect format (recordType/recordId)
      const targetType = r?.targetType || r?.recordType || "";
      const targetId = r?.targetId || r?.recordId || "";
      return { targetType, targetId };
    })
    .filter((r) => r.targetType && r.targetId);
}

/**
 * Convert RecordTarget objects to UserRoleOrEmailOption format for email delivery
 */
function recipientsToEmailOptions(recipients: any[]): any[] {
  return (recipients || [])
    .map((r: any) => {
      const targetType = r?.targetType || "";
      const targetId = r?.targetId || "";
      const target = r?.target;
      const isRole = targetType === "OnboardingJourneyNetworkRelation";
      const name = isRole ? target?.name || r?.name || "" : target?.fullName || target?.name || "";
      const label = isRole ? startCase(name) : name;

      return {
        recordType: targetType,
        recordId: targetId,
        id: userRoleEmailId(undefined, targetType, targetId, "work"),
        label,
        name,
        emailType: "work" as const,
      };
    })
    .filter((r: any) => r.recordType && r.recordId);
}

/**
 * Convert RecordTarget objects to composite ID strings for UserOrChannelSelectField
 */
function recipientsToCompositeIds(recipients: any[]): string[] {
  return (recipients || [])
    .map((r: any) => {
      if (typeof r === "string") return r;
      if (r?.targetType && r?.targetId) return toCompositeId(r.targetType, r.targetId);
      return null;
    })
    .filter(Boolean) as string[];
}

/**
 * Transform API response data to form data
 *
 * @param data - CheckpointTemplate from API response
 * @returns CheckpointTemplateSchema ready for form initialization
 */
export function toCheckpointTemplateFormData(data: CheckpointTemplate): CheckpointTemplateSchema {
  const { evaluateAtTrigger, paths = [], recipients = [], ...rest } = data;

  // For message delivery, convert RecordTarget objects to composite ID strings
  // since UserOrChannelSelectField expects string arrays.
  // For email delivery, convert to UserRoleOrEmailOption format.
  const formRecipients =
    data.deliveryMethod === "message" ? recipientsToCompositeIds(recipients) : recipientsToEmailOptions(recipients);

  return {
    ...rest,
    evaluateAtTrigger,
    paths,
    recipients: formRecipients,
    checkTarget: data.checkTarget ?? "new_hire_tasks",
    deliveryMethod: data.deliveryMethod ?? "message",
    allowWeekends: data.allowWeekends ?? false,
  };
}

/**
 * Transform form data to API create payload
 * Removes form-only fields and ensures correct structure for API
 */
export function toCreateCheckpointTemplatePayload(formData: CheckpointTemplateSchema): CreateCheckpointTemplatePayload {
  const { paths, recipients, ...rest } = formData;

  return {
    ...rest,
    recipients: normalizeRecipients(recipients as any[]),
  } as unknown as CreateCheckpointTemplatePayload;
}

/**
 * Transform form data to API update payload
 * Same as create but all fields optional
 */
export function toUpdateCheckpointTemplatePayload(
  formData: Partial<CheckpointTemplateSchema>,
): UpdateCheckpointTemplatePayload {
  const { paths, recipients, ...rest } = formData;

  return {
    ...rest,
    ...(recipients ? { recipients: normalizeRecipients(recipients as any[]) } : {}),
  } as UpdateCheckpointTemplatePayload;
}
